import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { Text, IconButton, TooltipHost } from 'office-ui-fabric-react';
import { get, differenceBy } from 'lodash';
import { Toggle } from 'office-ui-fabric-react/lib/Toggle';
import { FontIcon } from 'office-ui-fabric-react/lib/Icon';
import { PrimaryButton } from 'office-ui-fabric-react/lib/Button';
import Module from '../../components/Module';
import ModuleContentList from '../../components/ModuleContentList';
import PageTitle from '../../components/PageTitle';
import Main from '../../components/Main';
import SearchBar from '../../components/SearchBar';
import { fetchAllRoles } from '../../actions/roles';
import Link from '../../components/Link';
import TextButton from '../../components/TextButton';
import SavingIndicator from '../../components/SavingIndicator';
import PermissionSearchList from '../../components/PermissionSearchList';
import styles from './AddRolePermissions.style';

const AddRolePermissions = () => {
  const dispatch = useDispatch();
  const { isEdit, roleFormData } = useLocation();
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectAllItems, setSelectAllItems] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const history = useHistory();

  const permissions = useSelector(state =>
    Object.keys(get(state, 'entities.roles.admin.permissions', [])).map(item => ({
      permission: item,
      isEnabled: true,
    })),
  );

  useEffect(() => {
    const loadData = async () => {
      await dispatch(fetchAllRoles());
      setIsLoading(false);
    };
    if (roleFormData?.permissions?.length) {
      setSelectedItems(roleFormData.permissions);
    }
    if (!permissions?.length) {
      loadData();
    } else {
      setIsLoading(false);
    }
  }, [roleFormData]);

  const handleSearch = query => {
    const searchItem = permissions.filter(i =>
      i.permission.toLowerCase().includes(query.toLowerCase()),
    );
    setSearchResults(searchItem);
    setShowSearchResults(true);
  };

  const handleSearchClear = () => {
    setSearchResults([]);
    setShowSearchResults(false);
  };

  const handleSelectAllButton = async () => {
    const items = showSearchResults ? searchResults : permissions;
    const newItems = items.filter(
      item => !selectedItems.some(selected => selected.permission === item.permission),
    );
    setSelectedItems([...selectedItems, ...newItems]);
    setSelectAllItems(true);
  };

  const handleDeselectAndRemoveAll = async () => {
    if (roleFormData) setSelectedItems([...roleFormData.permissions]);
    else setSelectedItems([]);
    setSelectAllItems(false);
  };

  const onHandleColumnClick = () => {
    if (selectAllItems) {
      if (roleFormData) setSelectedItems([...roleFormData.permissions]);
      else setSelectedItems([]);
      setSelectAllItems(false);
    } else {
      const items = showSearchResults ? searchResults : permissions;
      const newItems = items.filter(
        item => !selectedItems.some(selected => selected.permission === item.permission),
      );
      setSelectedItems([...selectedItems, ...newItems]);
      setSelectAllItems(true);
    }
  };

  const onSelectItems = item => {
    if (
      selectedItems.length > 0 &&
      selectedItems.some(checkedItem => checkedItem.permission === item.permission)
    ) {
      setSelectedItems(
        selectedItems.filter(checkedItem => checkedItem.permission !== item.permission),
      );
      // Uncheck the column header select products if the selected products lengh becomes zero.
      setSelectAllItems(false);
    } else {
      setSelectedItems([...selectedItems, item]);
      if (selectedItems.length === permissions.length) {
        setSelectAllItems(true);
      }
    }
  };

  const previousPathname = isEdit
    ? `/permissions/roles/${roleFormData?.slug}`
    : '/permissions/add-role';
  const newlySelectedItems = differenceBy(selectedItems, roleFormData?.permissions || []);

  // handle adding items to the staged list
  const handleSave = async () => {
    setIsSubmitting(true);

    history.push({
      pathname: previousPathname,
      roleFormData: { ...roleFormData, permissions: selectedItems },
    });
    setIsSubmitting(false);
  };

  const handleCancel = () => {
    history.push({ pathname: previousPathname, roleFormData });
  };

  const handleToggleChange = (e, checked, permissionItem) => {
    const modifiedPermissions = selectedItems.map(item => {
      const modifyItem = item;
      if (modifyItem.permission === permissionItem.permission) {
        modifyItem.isEnabled = checked;
      }
      return modifyItem;
    });
    setSelectedItems(modifiedPermissions);
  };

  const selectMenuProps = {
    items: [
      {
        key: `selectPermission`,
        text: `Select To Add permission to role`,
        iconProps: { iconName: 'LocationCircle' },
      },
    ],
  };

  const itemsColumns = [
    {
      key: 'select',
      name: 'select',
      headerClassName: styles.iconColumnHeaderSelectButton,
      arianame: 'icon',
      iconName: selectAllItems ? 'SkypeCircleCheck' : '',
      isIconOnly: true,
      fieldName: 'select',
      minWidth: 20,
      maxWidth: 20,
      onColumnClick: onHandleColumnClick,
      onRender: (item, index) => {
        const permission = get(item, 'permission');
        const iconName = selectedItems.some(items => {
          let selected = false;
          const somePermission = get(items, 'permission');
          if (somePermission === permission) selected = true;
          return selected;
        })
          ? 'CompletedSolid'
          : '';
        const alreadyIncluded =
          roleFormData?.permissions &&
          roleFormData.permissions.some(items => {
            let selected = false;
            const someItemsName = get(items, 'permission');
            if (someItemsName === permission) selected = true;
            return selected;
          });
        return (
          <TooltipHost
            onTooltipToggle={false}
            content={alreadyIncluded ? `Item Included` : ''}
            calloutProps={{
              gapSpace: 0,
            }}
          >
            <IconButton
              ariaLabel="select"
              className={alreadyIncluded ? styles.selectedButton : styles.selectButton}
              menuProps={selectMenuProps}
              iconProps={{ iconName }}
              menuIconProps={{ iconName: '' }}
              id={`selectIcon${index}`}
              onMenuClick={
                alreadyIncluded
                  ? event => event.preventDefault()
                  : event => {
                      event.preventDefault();
                      event.stopPropagation();
                      onSelectItems(item);
                    }
              }
            />
          </TooltipHost>
        );
      },
    },
    {
      key: 'icon',
      name: 'icon',
      headerClassName: styles.iconColumnHeader,
      ariaLabel: 'icon',
      isIconOnly: true,
      fieldName: 'icon',
      minWidth: 14,
      maxWidth: 14,
      columnActionsMode: 0,
      onRender: () => {
        return (
          <div className={styles.iconColumn}>
            <FontIcon
              iconName="PlayerSettings"
              ariaLabel="PlayerSettings"
              className={styles.detailRowIconColor}
            />
          </div>
        );
      },
    },
    {
      key: 'permission',
      name: 'Permissions',
      fieldName: 'permission',
      minWidth: 100,
      maxWidth: 200,
      columnActionsMode: 0,
      isPadded: true,
    },
  ];

  const stagingColumns = [
    {
      key: 'icon',
      name: 'icon',
      headerClassName: styles.iconColumnHeader,
      ariaLabel: 'icon',
      isIconOnly: true,
      fieldName: 'icon',
      minWidth: 14,
      maxWidth: 14,
      columnActionsMode: 0,
      onRender: () => {
        return (
          <div className={styles.iconColumn}>
            <FontIcon
              iconName="PlayerSettings"
              ariaLabel="PlayerSettings"
              className={styles.detailRowIconColor}
            />
          </div>
        );
      },
    },
    {
      key: 'permission',
      name: 'Permissions',
      fieldName: 'permission',
      minWidth: 500,
      maxWidth: 550,
      columnActionsMode: 0,
      isPadded: true,
    },
    {
      key: 'toggle',
      name: 'toggle',
      headerClassName: styles.iconColumnHeader,
      ariaLabel: 'toggleIcon',
      isIconOnly: true,
      minWidth: 90,
      maxWidth: 90,
      columnActionsMode: 0,
      onRender: item => {
        const permissionItem = selectedItems.find(
          selectedItem => selectedItem.permission === item.permission,
        );
        return (
          <div className={styles.toggleColumn}>
            <Toggle
              checked={permissionItem.isEnabled}
              onText="Enabled"
              offText="Disabled"
              onChange={(e, checked) => handleToggleChange(e, checked, item)}
              className={styles.pill}
            />
          </div>
        );
      },
    },
    {
      key: 'remove',
      name: 'Remove',
      arianame: 'remove',
      isIconOnly: true,
      fieldName: 'remove',
      headerClassName: styles.iconColumnHeader,
      minWidth: 25,
      maxWidth: 25,
      isPadded: true,
      onRender: (item, index) => {
        const iconName = 'Cancel';
        return (
          <IconButton
            className={styles.removeButton}
            menuProps={selectMenuProps}
            iconProps={{ iconName }}
            menuIconProps={{ iconName: '' }}
            ariaLabel={iconName}
            id={`removeButton${index}`}
            onMenuClick={event => {
              event.preventDefault();
              event.stopPropagation();
              onSelectItems(item);
            }}
          />
        );
      },
    },
  ];

  return (
    <>
      <PageTitle>
        <Link to={{ pathname: previousPathname, roleFormData }}>
          {roleFormData?._id ? 'Edit' : 'Create'} Role
        </Link>
        <span className={styles.slash}> / </span>
        Permission Search
      </PageTitle>

      <Main>
        <div className={styles.searchFieldsContainer}>
          <SearchBar
            placeholder="Search"
            ariaLabel="Search"
            underlined
            onChange={query => handleSearch(query)}
            onClear={() => handleSearchClear()}
            data-testid="search-input"
            className={styles.search}
          />
        </div>
        <div className={styles.resultsContainer}>
          {showSearchResults ? (
            <div>
              <Text as="h4" block>
                {searchResults.length || 'No'} Results
              </Text>
            </div>
          ) : (
            <div />
          )}
          <div className={styles.selectDeselectButtonsContainer}>
            {newlySelectedItems.length === 0 && (
              <TextButton
                className={styles.textButton}
                onClick={() => handleSelectAllButton()}
                data-testid="select-all"
                disabled={permissions.length === 0}
              >
                Select All
              </TextButton>
            )}
            {newlySelectedItems.length > 0 && (
              <TextButton
                className={styles.textButton}
                onClick={() => handleDeselectAndRemoveAll()}
                data-testid="deselect-all"
              >
                Deselect All
              </TextButton>
            )}
          </div>
        </div>
        <Module>
          <ModuleContentList>
            <PermissionSearchList
              columns={itemsColumns}
              items={showSearchResults ? searchResults : permissions}
              selectedItems={selectedItems}
              onHandleColumnClick={onHandleColumnClick}
              disableRowLink
              shimmered
              enableShimmer={isLoading}
              className={styles.permissionList}
            />
          </ModuleContentList>
        </Module>
        {/* Only show staging area if new items are selected */}
        {newlySelectedItems.length !== 0 && (
          <div>
            <div className={styles.resultsContainer}>
              <div className={styles.resultsMessage}>
                <Text as="h4" block>
                  {newlySelectedItems.length === 0
                    ? ''
                    : `${newlySelectedItems.length} Selected ${
                        newlySelectedItems.length > 1 ? 'Items' : 'Item'
                      } `}
                </Text>
              </div>
              <div className={styles.selectDeselectButtonsContainer}>
                <TextButton
                  className={styles.textButton}
                  onClick={() => handleDeselectAndRemoveAll()}
                  data-testid="remove-all"
                >
                  Remove All
                </TextButton>
              </div>
            </div>
            <Module>
              <ModuleContentList>
                <PermissionSearchList
                  columns={stagingColumns}
                  items={newlySelectedItems}
                  className={styles.permissionList}
                  disableRowLink
                  noBorderBottom
                />
              </ModuleContentList>
            </Module>
          </div>
        )}
        <div className={styles.buttonsContainer}>
          <TextButton
            className={styles.cancelButton}
            onClick={handleCancel}
            disabled={isSubmitting}
            data-testid="permissionSearchCancel"
          >
            Cancel
          </TextButton>
          {!isSubmitting ? (
            <PrimaryButton
              type="submit"
              onClick={handleSave}
              disabled={isSubmitting || newlySelectedItems.length === 0}
            >
              Add Permissions
            </PrimaryButton>
          ) : (
            <SavingIndicator />
          )}
        </div>
      </Main>
    </>
  );
};

export default AddRolePermissions;
